import React, { ComponentProps } from 'react';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';
import { StyleSheet, TextStyle, View, ViewStyle } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Button, DisplayText, Icon, TitleText, useResponsive } from '@garnish/core';
import { Image } from '@garnish/media';

import { LoyaltyHomeProgressBar } from '@sg/shared-fe-features-loyalty';

import { LoyaltyHomeChallengesRewardsContainer } from '../LoyaltyHomeChallengesRewardsContainer';

export const LoyaltyHomeReturnHero = (props: LoyaltyHomeReturnHeroProps) => {
  const {
    palette,
    title,
    firstName,
    availablePoints,
    totalPoints,
    pointsRewards,
    backgroundImage,
    backgroundImageAlt,
    onViewRewards,
    onOrderToEarn,
  } = props;

  const { formatNumber } = useIntl();
  const { match } = useResponsive();
  const buttonSize = match(['medium' as const, 'medium' as const, 'large' as const]);
  const goatSize = match([48, 48, 60]);

  return (
    <View style={styles.container}>
      <Image
        contentFit="cover"
        style={styles.image}
        transition={theme.transitions.base}
        source={{ uri: backgroundImage }}
        alt={backgroundImageAlt}
      />

      <LoyaltyHomeChallengesRewardsContainer style={styles.content}>
        <View style={styles.titleWrapper}>
          <Icon name="IconSgRewardWithText2" color={theme.colors.SWEET_CORN} width={160} />

          <TitleText style={styles.title} sizeMatch={['64']}>
            {title}
          </TitleText>
        </View>

        <View style={styles.pointsWrapper}>
          <View style={[styles.pointsContent, palettes[palette].pointsBackground]}>
            {palette === 'dark' ? (
              <View style={styles.goatWrapper}>
                <Icon
                  name="IconGoat"
                  color={theme.colors.WHITE}
                  width={goatSize}
                  height={goatSize}
                />
              </View>
            ) : null}

            <BodyText style={palettes[palette].welcome} sizeMatch={['24']}>
              <FormattedMessage {...messages.welcome} values={{ customer: firstName }} />
            </BodyText>

            <DisplayText style={palettes[palette].points} sizeMatch={['56']}>
              <FormattedMessage
                {...messages.points}
                values={{ points: formatNumber(availablePoints) }}
              />
            </DisplayText>

            <LoyaltyHomeProgressBar
              customSpacing={0}
              onlyTerminalDots
              barStyle={styles.barStyle}
              palette={palette}
              availablePoints={availablePoints}
              totalPoints={totalPoints}
              pointsRewards={pointsRewards}
            />

            <View style={styles.ctaWrapper}>
              <Button
                size={buttonSize}
                palette={palettes[palette].viewRewardsCta}
                onPress={onViewRewards}
              >
                <FormattedMessage {...messages.viewRewards} />
              </Button>

              <Button
                size={buttonSize}
                palette={palettes[palette].orderToEarnCta}
                onPress={onOrderToEarn}
              >
                <FormattedMessage {...messages.orderToEarn} />
              </Button>
            </View>
          </View>
        </View>
      </LoyaltyHomeChallengesRewardsContainer>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 600,
    backgroundColor: theme.colors.KALE,
  },
  image: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.KALE,
  },
  content: {
    flex: 1,
    minHeight: 600,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.OPACITY.TRANSPARENT,
  },
  titleWrapper: {
    flex: 1,
    gap: theme.spacing['6'],
  },
  title: {
    color: theme.colors.SWEET_CORN,
  },
  pointsWrapper: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  pointsContent: {
    width: '100%',
    padding: theme.spacing['8'],
    borderRadius: theme.radius.xlarge,
    gap: theme.spacing['6'],
  },
  goatWrapper: {
    position: 'absolute',
    top: theme.spacing['6'],
    right: theme.spacing['6'],
  },
  ctaWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
  },
  barStyle: {
    minHeight: 16,
    borderRadius: 16,
  },
  backgroundWhite: {
    backgroundColor: theme.colors.WHITE,
  },
  backgroundBlack: {
    backgroundColor: theme.colors.BLACK,
  },
  textWhite: {
    color: theme.colors.WHITE,
  },
  textBlack: {
    color: theme.colors.BLACK,
  },
  textKale: {
    color: theme.colors.KALE,
  },
  textSpinach: {
    color: theme.colors.SPINACH,
  },
});

// ─── Palettes ────────────────────────────────────────────────────────────────

const palettes: Record<LoyaltyHomeReturnHeroPalette, PaletteObject> = {
  light: {
    welcome: styles.textBlack,
    points: styles.textSpinach,
    milestone: styles.textKale,
    pointsBackground: styles.backgroundWhite,
    viewRewardsCta: 'secondary',
    orderToEarnCta: 'kaleLightest',
  },
  dark: {
    welcome: styles.textWhite,
    points: styles.textWhite,
    milestone: styles.textWhite,
    pointsBackground: styles.backgroundBlack,
    viewRewardsCta: 'khakiMuted',
    orderToEarnCta: 'white',
  },
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly welcome: { readonly customer: MessageValue };
  readonly points: { readonly points: MessageValue };
  readonly viewRewards: NoMessageValues;
  readonly orderToEarn: NoMessageValues;
}>({
  welcome: {
    defaultMessage: 'Hi {customer}, you have',
    description: 'Loyalty Home Return Hero | Welcome',
  },
  points: {
    defaultMessage: '{points} points',
    description: 'Loyalty Home Return Hero | Points',
  },
  viewRewards: {
    defaultMessage: 'View rewards',
    description: 'Loyalty Home Return Hero | View Rewards',
  },
  orderToEarn: {
    defaultMessage: 'Order to earn',
    description: 'Loyalty Home Return Hero | Order to Earn',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LoyaltyHomeReturnHeroPalette = 'light' | 'dark';

type PaletteObject = {
  welcome: TextStyle;
  points: TextStyle;
  milestone: TextStyle;
  pointsBackground: ViewStyle;
  viewRewardsCta: ComponentProps<typeof Button>['palette'];
  orderToEarnCta: ComponentProps<typeof Button>['palette'];
};

type PointsReward = {
  id: string;
  points?: number | null;
};

type LoyaltyHomeReturnHeroProps = {
  palette: LoyaltyHomeReturnHeroPalette;
  title: string;
  firstName: string;
  availablePoints: number;
  totalPoints: number;
  pointsRewards: readonly PointsReward[];
  backgroundImage: string;
  backgroundImageAlt: string;
  onViewRewards: () => void;
  onOrderToEarn: () => void;
};
